import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { postsAPI } from '../../services/api';
import { Card, Button, StatusBadge } from '../ui';
import Loading from '../common/Loading';
import { formatDate, handleApiError, getImageUrl } from '../../utils/helpers';
import { PLATFORMS, CONTENT_TYPES } from '../../utils/platformConfig';
import { PlatformIcon } from '../icons/PlatformIcons';
import { ArrowLeft, MonitorSmartphone, Calendar, User, MessageSquare } from 'lucide-react';

const PostReviewSimpleB = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isClient } = useAuth();
  const { isRTL } = useLanguage();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await postsAPI.getById(id);
        const postData = response.data.post;

        if (postData.current_image_url) {
          postData.current_image_url = getImageUrl(postData.current_image_url);
        }

        setPost(postData);
      } catch (err) {
        setError(handleApiError(err));
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error || !post) {
    return (
      <div className="max-w-4xl mx-auto py-16 text-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <p className="text-red-600 mb-4">{error || (isRTL ? 'لم يتم العثور على البوست' : 'Post not found')}</p>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          {isRTL ? 'رجوع' : 'Back'}
        </Button>
      </div>
    );
  }

  const platformInfo = post.platform ? PLATFORMS[post.platform] : null;
  const contentTypeInfo = post.content_type ? CONTENT_TYPES[post.content_type] : null;

  return (
    <div className="max-w-5xl mx-auto space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Card className="p-5 bg-gradient-to-r from-violet-50 via-white to-sky-50 border-slate-200">
        <div className="flex items-start justify-between gap-4">
          <div className={`flex items-start gap-3 flex-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-white/80 transition-colors mt-1"
            >
              <ArrowLeft className={`w-5 h-5 text-slate-600 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <div className="inline-flex items-center gap-2 mb-1">
                <MonitorSmartphone className="w-4 h-4 text-violet-600" />
                <span className="text-xs font-semibold uppercase tracking-wide text-violet-600">
                  {isRTL ? 'تصميم تجريبي B' : 'Experimental Layout B'}
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">
                {post.title || (isRTL ? 'بوست بدون عنوان' : 'Untitled Post')}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-600">
                {post.client_name && (
                  <span className="inline-flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {post.client_name}
                  </span>
                )}
                {post.created_at && (
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(post.created_at)}
                  </span>
                )}
                <StatusBadge status={post.status} />
              </div>
            </div>
          </div>

          {/* Link to full page */}
          <div className="flex items-start">
            <Link to={`/posts/${post.id}`}>
              <Button variant="secondary" size="sm">
                {isRTL ? 'الصفحة الكاملة' : 'Full Page'}
              </Button>
            </Link>
          </div>
        </div>
      </Card>

      {/* Top full-width preview */}
      <Card className="overflow-hidden rounded-2xl border-slate-200">
        <div className="bg-slate-900 flex flex-col md:flex-row">
          <div className="flex-1 flex items-center justify-center bg-slate-950">
            {post.current_image_url ? (
              <img
                src={post.current_image_url}
                alt={post.title || 'Post'}
                className="max-h-[420px] w-auto object-contain"
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-slate-400 py-16">
                <MonitorSmartphone className="w-10 h-10 mb-3" />
                <p className="text-sm">
                  {isRTL ? 'لا توجد صورة حالية لهذا البوست' : 'No current image for this post'}
                </p>
              </div>
            )}
          </div>
          <div className="w-full md:w-80 p-5 bg-slate-900/95 text-slate-100 flex flex-col gap-3">
            {platformInfo && (
              <div className="flex items-center gap-2 text-sm">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-800">
                  <PlatformIcon platformId={platformInfo.id} className="w-4 h-4 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-slate-400">
                    {isRTL ? 'المنصة' : 'Platform'}
                  </span>
                  <span className="font-medium">
                    {platformInfo.icon}{' '}
                    {isRTL ? platformInfo.nameAr : platformInfo.name}
                  </span>
                </div>
              </div>
            )}
            {contentTypeInfo && (
              <div className="flex flex-col text-sm">
                <span className="text-xs text-slate-400">
                  {isRTL ? 'نوع المحتوى' : 'Content Type'}
                </span>
                <span className="inline-flex items-center gap-1 font-medium">
                  {contentTypeInfo.icon}
                  {isRTL ? contentTypeInfo.nameAr : contentTypeInfo.name}
                </span>
              </div>
            )}
            {post.serial_number && (
              <div className="flex flex-col text-sm">
                <span className="text-xs text-slate-400">
                  {isRTL ? 'رقم البوست' : 'Post #'}
                </span>
                <span className="font-medium">{post.serial_number}</span>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Bottom content: caption + notes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-5 rounded-2xl border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-violet-600" />
            <h2 className="text-sm font-semibold text-slate-800">
              {isRTL ? 'الكابتشن' : 'Caption'}
            </h2>
          </div>
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 max-h-56 overflow-y-auto">
            <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
              {post.current_caption ||
                (isRTL ? 'لا يوجد كابتشن حالي' : 'No current caption')}
            </p>
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="p-5 rounded-2xl border-slate-200 shadow-sm space-y-2 text-sm text-slate-700">
            <h2 className="text-sm font-semibold text-slate-800 mb-2">
              {isRTL ? 'معلومات إضافية' : 'Additional Info'}
            </h2>
            {post.schedule_date && (
              <div className="flex items-center justify-between">
                <span className="text-slate-500">
                  {isRTL ? 'تاريخ الجدولة' : 'Scheduled at'}
                </span>
                <span className="font-medium">
                  {formatDate(post.schedule_date)}
                </span>
              </div>
            )}
            {post.approved_at && (
              <div className="flex items-center justify-between">
                <span className="text-slate-500">
                  {isRTL ? 'تاريخ التعميد' : 'Approved at'}
                </span>
                <span className="font-medium">
                  {formatDate(post.approved_at)}
                </span>
              </div>
            )}
            {post.assigned_designer_name && (
              <div className="flex items-center justify-between">
                <span className="text-slate-500">
                  {isRTL ? 'المصمم' : 'Designer'}
                </span>
                <span className="font-medium">
                  {post.assigned_designer_name}
                </span>
              </div>
            )}
            {post.assigned_writer_name && (
              <div className="flex items-center justify-between">
                <span className="text-slate-500">
                  {isRTL ? 'الكاتب' : 'Writer'}
                </span>
                <span className="font-medium">
                  {post.assigned_writer_name}
                </span>
              </div>
            )}
          </Card>

          {!isClient && (
            <Card className="p-4 rounded-2xl border-dashed border-slate-300 text-xs text-slate-500">
              {isRTL
                ? 'تصميم B يركز على معاينة كاملة في الأعلى مع تفاصيل مختصرة أسفلها. فقط للمعاينة والتجربة.'
                : 'Layout B focuses on a full-width preview on top with compact details below. For preview/testing only.'}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostReviewSimpleB;

